import { StyleGuideLite } from "@intelligems/ig-embedded-preview/messages";
import { v4 } from "uuid";

import { WORKFLOW_IDS } from "@intelligems/ig-plugin/utils/workflow-ids";
import { ChatWorkflowRequestBodyDto } from "@intelligems/ig-types/dtos/ai/workflows/chat-workflow";
import { UiStreamAction } from "@intelligems/ig-types/schemas/ai/ui-stream-actions";
import { UserInput } from "@intelligems/ig-types/schemas/ai/node-execution";
import { VisualBuilderAIVariables } from "@intelligems/ig-types/schemas/visual-builder/variables";
import {
  VisualBuilderDevice,
  VisualBuilderViewport,
  VisualBuilderViewports,
} from "@intelligems/ig-types/schemas/visual-builder/viewports";
import { createUserMessage } from "@src/features/ai/components/AiChat/utils/workflowUtils";
import { PREVIEW_IFRAME_ID } from "@src/features/experienceBuilder/constants";
import { createAsyncThunk } from "@src/store";
import {
  getAiChat,
  getIsNewSession,
  handleAiChatAction,
} from "@src/store/slices/aiChat";
import { experienceBuilderAiSlice } from "@src/store/slices/experienceBuilder/ai/slice";
import { getAiMods } from "@src/store/slices/experienceBuilder/common/selectors/getAiMods";
import { getTurnSelection } from "@src/store/slices/experienceBuilder/common/selectors/getTurnSelection";
import { isOnsiteUpsellBuilder } from "@src/store/slices/experienceBuilder/common/selectors/isOnsiteUpsellBuilder";
import {
  PageHtmlResult,
  requestPageHtml,
} from "@src/store/slices/experienceBuilder/common/thunks/requestPageHtml";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { ExperienceBuilderStats } from "@src/store/slices/experienceBuilder/types";
import { selectedExperience } from "@src/store/slices/experiences";
import { getOrganization } from "@src/store/slices/organization";
import { getIsAgenticLogicEnabled } from "@src/store/slices/organization/selectors/getIsAgenticLogicEnabled";
import { selectedVariations } from "@src/store/slices/variations";

/**
 * What kind of turn we're firing. The thunk assembles a different shape of
 * `ChatWorkflowRequestBodyDto` depending on the kind (fresh user-typed turn
 * vs. synthetic continuation driven by a prior tool call).
 */
export type PrepareChatMutationKind =
  | { kind: "user-first"; input: UserInput }
  | { kind: "continuation-user"; message: string }
  | { kind: "continuation-auto-verify" }
  | { kind: "continuation-tool-result"; toolCallId: string; output: unknown };

// The nominal `VisualBuilderViewports` size only matches what's on screen for
// mobile (the preview is capped at 390px). Desktop has no max width — the
// preview is as wide as the builder window allows — so the static 1440 would
// misreport what the model actually sees and screenshots. Measure the live
// preview iframe instead, falling back to the nominal size only when it isn't
// mounted (its content is cross-origin, but the element's own box is readable).
const measureViewport = (device: string): VisualBuilderViewport => {
  const fallback =
    VisualBuilderViewports[device as VisualBuilderDevice] ??
    VisualBuilderViewports.mobile;
  const rect = document
    .getElementById(PREVIEW_IFRAME_ID)
    ?.getBoundingClientRect();
  if (!rect) {
    return fallback;
  }
  const width = Math.round(rect.width);
  const height = Math.round(rect.height);
  // A sub-pixel rect passes a raw `> 0` check but rounds to 0 — guard the
  // rounded values so we never ship a 0×0 viewport.
  if (width < 1 || height < 1) {
    return fallback;
  }
  return { name: fallback.name, width, height };
};

const {
  getVariationId,
  getDevice,
  getLastPath,
  getStats,
  getCachedPageHtml,
  getDemoOrigin,
  getInitialComponentType,
} = experienceBuilderSlice.selectors;

/** Per-turn preview metrics drained from `stats` into the request variables. */
function buildClientMetrics(stats: ExperienceBuilderStats) {
  const { loadTimes, navigateCount, startSessionTs } = stats;
  const averageLoadTime =
    loadTimes.length > 0
      ? Math.round(loadTimes.reduce((sum, t) => sum + t, 0) / loadTimes.length)
      : 0;
  // Elapsed since session start; 0 before the session clock is anchored.
  const sessionDuration = startSessionTs > 0 ? Date.now() - startSessionTs : 0;
  return { navigateCount, averageLoadTime, sessionDuration };
}

/**
 * Assembles the full request payload for the modifications-chat workflow.
 * Reads experience-builder + aiChat state, fetches fresh page HTML from the
 * preview iframe, dispatches any required chat-state side effects
 * (`initialize` / `addMessage` / `resetAutoVerifyAttempt`), then returns the
 * assembled body.
 *
 * Why a thunk-return rather than a state slot: the payload is used exactly
 * once (to call `chatWorkflow.mutate()` in the view) and doesn't need to
 * persist. Returning via `.unwrap()` keeps Redux state free of transient
 * request payloads and avoids extra set/clear action round-trips.
 */
/** The style guide as Viv is given it: everything but the font files. */
type StyleGuideForPrompt = Omit<StyleGuideLite, "typography"> & {
  typography: Omit<StyleGuideLite["typography"], "fontFaces">;
};

/**
 * The style guide minus what the model cannot use.
 *
 * `fontFaces` carries the URLs of the store's actual font files, added for the
 * audit's type specimens — it needs to *render* the face. Viv only reasons
 * about styling, so a list of woff2 URLs is a few hundred tokens of payload it
 * can do nothing with, resent on every turn because this blob lives in
 * per-turn `variables`.
 *
 * The key is dropped rather than emptied. `fontFaces: []` is what the extractor
 * returns when it looked and the page declares none, so sending it here would
 * tell Viv something false about the store instead of telling it nothing.
 *
 * Trimmed here rather than at extraction: the extractor's job is to report
 * what the page has, and each consumer decides what its own model is given.
 */
const forPrompt = (guide: StyleGuideLite): StyleGuideForPrompt => {
  const { fontFaces: _fontFaces, ...typography } = guide.typography;
  return { ...guide, typography };
};

export const prepareChatMutation = createAsyncThunk<
  ChatWorkflowRequestBodyDto,
  PrepareChatMutationKind
>(
  "experienceBuilderAi/prepareChatMutation",
  async (kind, { dispatch, getState }) => {
    const state = getState();
    const aiChat = getAiChat(state);
    const variationId = getVariationId(state);
    const device = getDevice(state);
    const path = getLastPath(state) ?? "";
    // Undefined unless a demo URL is set. Re-read every turn so a mid-session
    // demo-URL change reaches the Shopify tools on the next request.
    const demoOrigin = getDemoOrigin(state);

    const viewport = measureViewport(device);
    const aiMods = getAiMods(state);
    // Only the merchant's own turn carries the selection. Continuation turns
    // omit it so a re-select during an auto-verify cycle can't retarget a task
    // already in flight.
    const selection =
      kind.kind === "user-first" ? getTurnSelection(state) : undefined;
    const experience = selectedExperience(state);
    const variations = selectedVariations(state);
    const organization = getOrganization(state);
    const version =
      import.meta.env.VITE_WF_VERSION_MODIFICATIONS_CHAT || undefined;

    if (!organization?.id) {
      throw new Error(
        "prepareChatMutation: cannot fire without a loaded organization"
      );
    }

    // Per-kind setup (validation, action dispatch, input shaping). Runs
    // before the page-html fetch so we don't pay for it on early failures.
    let sessionId: string;
    let input: UserInput;
    let isNewSession = false;
    let inputMessageId: string | undefined;

    // A parked `execution: "client"` tool resumes the suspended node in place —
    // answered with a `tool-result`, not a graph restart (so no
    // `restartFromFirstNode`). It still carries `variables` below: the backend
    // never persists the rrweb snapshot (it's stripped every turn for storage),
    // so a resume that omitted it would leave the resumed node's
    // `visual_builder_screenshot` rendering a blank `about:blank`. The client
    // tools that trigger this (`locate`/`evaluate_js`) are read-only, so the
    // cached snapshot from the initiating turn is still accurate.
    const isToolResult = kind.kind === "continuation-tool-result";

    if (kind.kind === "user-first") {
      isNewSession = getIsNewSession(state);
      sessionId = aiChat.sessionId || v4();
      input = kind.input;

      // Stable id shared between the optimistic user bubble and the server
      // echo so the reducer reconciles by id instead of appending a duplicate.
      inputMessageId = v4();
      const { message } = createUserMessage(kind.input);
      if (message) {
        message.id = inputMessageId;
      }
      const nodeId = v4();
      const turnId = v4();

      // Fresh user turn — discard any in-flight verify cycle counter.
      dispatch(experienceBuilderAiSlice.actions.resetAutoVerifyAttempt());

      const actions: UiStreamAction[] = [];
      if (isNewSession) {
        actions.push({
          type: "initialize",
          workflowId: WORKFLOW_IDS.MODIFICATIONS_CHAT,
          sessionId,
          nodeId,
        });
      }
      if (message) {
        actions.push({
          type: "addMessage",
          turnId,
          message,
          nodeId,
          nodeName: "User Input",
        });
      }
      if (actions.length > 0) {
        dispatch(handleAiChatAction(actions));
      }
    } else {
      if (!aiChat.sessionId) {
        throw new Error(
          "prepareChatMutation: continuation requested with no active session"
        );
      }
      sessionId = aiChat.sessionId;
      if (kind.kind === "continuation-tool-result") {
        input = {
          type: "tool-result",
          toolCallId: kind.toolCallId,
          output: kind.output,
          metadata: { synthetic: true },
        };
      } else if (kind.kind === "continuation-auto-verify") {
        input = {
          type: "text",
          content: "<instruction>verify</instruction>",
          metadata: { synthetic: true },
        };
      } else {
        // `continuation-user` is always system-injected (e.g. "[system]
        // Preview switched..." after a turn-ending tool). Real user input
        // flows through `user-first` — never here. Mark synthetic so the
        // stream-handler hides it from the chat UI; persisted-history
        // restore filters it the same way.
        input = {
          type: "text",
          content: kind.message,
          metadata: { synthetic: true },
        };
      }
    }

    // A tool-result resume reuses the cached capture from the initiating turn
    // (its triggering client tools don't mutate the page), skipping a fresh
    // round-trip. Every other kind captures fresh; `requestPageHtml` rejects
    // with `PageHtmlUnavailableError` when capture is impossible (retries
    // exhausted, no cached fallback) — the caller catches and aborts the turn
    // behind the `pageHtmlUnavailable` overlay, so no broken-state turn ships.
    // A tool-result with no cache yet also falls back to a fresh capture.
    const cached = isToolResult ? getCachedPageHtml(state) : null;
    const html: PageHtmlResult = cached
      ? { ...cached, styleGuideLite: null }
      : await dispatch(requestPageHtml()).unwrap();

    // Drain this turn's preview metrics (navigations/loads since the last
    // request) and reset, so each turn reports only its own delta.
    const clientMetrics = buildClientMetrics(getStats(getState()));
    dispatch(experienceBuilderSlice.actions.resetTurnStats());

    // One-shot hand-off context from the creation chat, only on the session's
    // first turn. Read it before building variables and clear it immediately so
    // it can never ride a later turn or leak into an unrelated session.
    const creationBrief = isNewSession
      ? experienceBuilderAiSlice.selectors.getCreationBrief(state)
      : undefined;
    if (creationBrief) {
      dispatch(experienceBuilderAiSlice.actions.clearCreationBrief());
    }

    const variables: VisualBuilderAIVariables = {
      organization: { id: organization.id, name: organization.name },
      creationBrief,
      experience: {
        id: experience?.id,
        name: experience?.name ?? "",
        category: experience?.category,
        type: experience?.type,
        variations: (variations ?? []).map((v) => ({
          id: v.id,
          name: v.name,
          isControl: v.isControl,
        })),
      },
      context: {
        viewport,
        path,
        variationId,
        isOnsiteUpsell: isOnsiteUpsellBuilder(state),
        agenticLogicEnabled: getIsAgenticLogicEnabled(state),
        initialComponentType: getInitialComponentType(state) ?? undefined,
        mods: aiMods,
        ...(selection && { selection }),
      },
      // Sibling of `context` (not inside it): the Shopify tools read this
      // server-side, but it must stay out of the prompt-embedded `context`.
      demoOrigin,
      styleGuideLite: html.styleGuideLite
        ? JSON.stringify(forPrompt(html.styleGuideLite))
        : undefined,
      pageHtmlForPrompt: html.pageHtmlForPrompt,
      snapshot: html.snapshot,
      clientMetrics,
    };

    // A tool-result resume continues the parked node in place (no
    // `restartFromFirstNode`) but still carries `variables` so the re-sent
    // snapshot reaches the resumed node before it calls a screenshot.
    if (isToolResult) {
      return {
        sessionId,
        input,
        variables,
        version,
      };
    }

    // The server picks mode based on whether it finds the session:
    //   - session missing → "start" mode, reads `initialContext`
    //   - session found   → "continue" mode, reads `input` + `variables`
    // Send only what the matching mode consumes; `initialContext` on a
    // continue turn would be ignored (and carries the full snapshot).
    if (isNewSession) {
      return {
        sessionId,
        workflowId: WORKFLOW_IDS.MODIFICATIONS_CHAT,
        input,
        inputMessageId,
        initialContext: { variables },
        version,
      };
    }
    // A reply to a parked `request-user-input` question (Viv asking which
    // Agentic Logic rule to apply, or for approval to create one) must resume
    // the parked node so the answer lands on the open question; every other
    // follow-up starts a fresh walk, as before. The parked state is read here,
    // before the turn's own stream actions replace `nextAction`.
    const answersParkedQuestion =
      getAiChat(state).nextAction?.type === "user_input";
    return {
      sessionId,
      input,
      inputMessageId,
      variables,
      restartFromFirstNode: !answersParkedQuestion,
      version,
    };
  }
);
